/* eslint-disable react/jsx-props-no-spreading */
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import { Box, Stack, Typography, alpha } from '@mui/material';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { LocalizationProvider, StaticDatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';

import { useGetDatesQuery } from 'state/apiSlice';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import { getUniqueDatesFromSpecialDates } from 'state/utils';
import {
  selectProject,
  selectProjectSpecialDates,
  selectTargetDate,
  selectTargetEndDate,
  setStartEndDate,
  setTargetDate,
} from 'state/workflowSlice';

import dayjs, { Dayjs } from 'dayjs';
import DateTimePickerWrapper from 'features/common/DateTimeSelector/DateTimePickerWrapper';
import MultiDayRangePicker, {
  DayRangePickerButton,
} from 'features/common/DateTimeSelector/MultiDayRangePicker';
import SpecialDatesPicker, {
  SpecialDatesButton,
} from 'features/common/DateTimeSelector/SpecialDatesPicker';
import { DATE_PICKER_DEFAULT_SIZE } from 'features/common/DateTimeSelector/utils';
import FontAwesomeIcon from 'features/common/FontAwesomeIcon';
import StlPopoverWrapper from 'features/common/StlPopoverWrapper/StlPopoverWrapper';
import StlTooltip from 'features/common/StlTooltip';
import {
  daysInRange,
  isInRange,
  parseDateTimeIgnoringTz,
} from 'features/common/datetimeUtils';
import { classNames } from 'features/common/utils';
import { useNextLastDay } from 'features/workflow_nrt/useNextLastDay';
import { stl_gold_color_light, stl_realtime_color_light } from 'theme/cemTheme';

import { DatesList, DayInfo } from '../../appTypes';
import { OverflowLabel } from './OverflowLabel';
import { TaskBarComponent } from './TaskBar';

import 'features/task_bar/DatePicker.css';

import styles from './DatePicker.module.css';

export function findDate(targetDate_, datesInfo_): DayInfo | undefined {
  // Pretty horrible reverse seach for the selected day, this is a bit like doing .format() but probably worse
  for (let i = 0; i < datesInfo_.length; i++) {
    if (dayjs(datesInfo_[i].date).isSame(targetDate_)) {
      return datesInfo_[i];
    }
  }
  return undefined;
}

export function findRTDate(datesInfo_: DatesList): DayInfo | undefined {
  for (let i = datesInfo_.length - 1; i >= 0; i--) {
    if (!datesInfo_[i].is_complete_vnrt && !datesInfo_[i].is_nrt) {
      return datesInfo_[i];
    }
  }
  return undefined;
}

export function useDayInfo() {
  const project = useAppSelector(selectProject);
  const targetDate = useAppSelector(selectTargetDate);
  const { currentData: datesInfo } = useGetDatesQuery(project, {
    skip: !project,
  });
  let day_info;
  if (targetDate && datesInfo) {
    day_info = findDate(targetDate, datesInfo);
  }
  return { day_info };
}

function StlDay(
  props: PickersDayProps<Dayjs> & {
    vnrtOrRtDates: string[];
    nrtDates: string[];
    specialDates: Set<string>;
    endDate?: string;
    startDate?: string;
  },
) {
  const {
    endDate,
    startDate,
    vnrtOrRtDates,
    nrtDates,
    specialDates,
    day,
    outsideCurrentMonth,
    ...other
  } = props;

  const isVnrtOrRt =
    !outsideCurrentMonth &&
    vnrtOrRtDates.indexOf(day.format('YYYY-MM-DD')) >= 0;
  const isNrt =
    !outsideCurrentMonth && nrtDates.indexOf(day.format('YYYY-MM-DD')) >= 0;
  function routeToStyle() {
    const BORDER_WIDTH = 2;
    const BORDER_STYLE = 'solid';
    const HOVER_ALPHA = 0.7;
    const baseStyle = {
      '&.MuiPickersDay-today': {
        borderStyle: 'none',
      },
    };
    if (
      startDate &&
      endDate &&
      isInRange(
        day,
        parseDateTimeIgnoringTz(startDate),
        parseDateTimeIgnoringTz(endDate),
      ) &&
      (isVnrtOrRt || isNrt)
    )
      return {
        '&.MuiPickersDay-root': {
          backgroundColor: 'rgba(240, 207, 127, 0.20)',
          border: 'none',
        },
      };
    if (isVnrtOrRt) {
      return {
        '&.MuiPickersDay-root': {
          '&:hover': {
            backgroundColor: alpha(stl_realtime_color_light, HOVER_ALPHA),
            color: 'black',
          },
          '&.Mui-selected': {
            backgroundColor: stl_realtime_color_light,
          },
          borderColor: stl_realtime_color_light,
          borderWidth: BORDER_WIDTH,
          borderStyle: BORDER_STYLE,
        },
      };
    }

    if (isNrt) {
      return {
        '&.MuiPickersDay-root': {
          '&:hover': {
            backgroundColor: alpha(stl_gold_color_light, HOVER_ALPHA),
            color: 'black',
          },
          '&.Mui-selected': {
            backgroundColor: stl_gold_color_light,
          },
          borderColor: stl_gold_color_light,
          borderWidth: BORDER_WIDTH,
          borderStyle: BORDER_STYLE,
        },
      };
    }

    return { ...baseStyle };
  }

  const title = useMemo(() => {
    const formattedDate = day.toDate().toLocaleDateString('en-CA');
    return specialDates.has(formattedDate)
      ? 'You have added this date as a special event.'
      : undefined;
  }, [specialDates, day]);

  const picker = (
    <PickersDay
      {...other}
      outsideCurrentMonth={outsideCurrentMonth}
      day={day}
      sx={routeToStyle()}
    />
  );

  if (other.disabled) return picker;
  return (
    <StlTooltip title={title} placement="top">
      {picker}
    </StlTooltip>
  );
}
export interface IGenericNRTDatePicker {
  targetDate: string;
  setNewTargetDate: (value: string | undefined) => void;
  allowNoDate?: boolean;
  forbiddenDates?: string[];
  endDate?: string;
  onSelect?: () => void;
}

function GenericNRTDatePicker({
  targetDate,
  setNewTargetDate,
  forbiddenDates = [],
  endDate,
  onSelect,
  allowNoDate = false,
}: IGenericNRTDatePicker) {
  // datesList from REST api
  const project_slug = useAppSelector(selectProject);
  const specialDates = useAppSelector(selectProjectSpecialDates);
  const {
    data: rawDatesInfo,
    isLoading,
    isFetching,
    isError,
  } = useGetDatesQuery(
    project_slug,
    { skip: !project_slug, pollingInterval: 3 * 60 * 60 * 1000 }, // every 3 hours
  );

  // This is used for coloring only, and we want to include the "forbidden" dates
  const vnrtOrRtDates =
    rawDatesInfo &&
    rawDatesInfo
      .filter((dateInfo) => !dateInfo.is_nrt)
      .map((dateInfo) => dateInfo.date);
  const nrtDates =
    rawDatesInfo &&
    rawDatesInfo
      .filter((dateInfo) => dateInfo.is_nrt)
      .map((dateInfo) => dateInfo.date);

  // Filter out forbidden dates
  const datesInfo =
    rawDatesInfo &&
    rawDatesInfo.filter((info) => !forbiddenDates.includes(info.date));
  // memoize list of dates as strings
  const datesList = useMemo(
    () => (datesInfo ? datesInfo.map((info) => dayjs(info.date)) : []),
    [datesInfo],
  );

  const handleChange = (newTargetDate: dayjs.Dayjs) => {
    const foundDate = findDate(newTargetDate, datesInfo);
    if (foundDate) setNewTargetDate(foundDate.date);
  };
  useEffect(() => {
    if (datesInfo && datesInfo.length > 0) {
      if (allowNoDate) return;
      if (!targetDate) {
        console.log('DatePicker setting empty targetDate to first date');
        setNewTargetDate(datesInfo[datesInfo.length - 1].date);
      } else if (!findDate(dayjs(targetDate), datesInfo)) {
        console.log('DatePicker setting targetDate not available');
        setNewTargetDate(datesInfo[datesInfo.length - 1].date);
      }
    }
  }, [datesInfo, targetDate, allowNoDate]);

  const searchDate = (date) => {
    for (let i = 0; i < datesList.length; i++) {
      if (datesList[i].isSame(date)) {
        return true;
      }
    }
    return false;
  };

  const searchMonth = (date: dayjs.Dayjs) => {
    for (let i = 0; i < datesList.length; i++) {
      if (
        datesList[i].year() === date.year() &&
        datesList[i].month() === date.month()
      ) {
        return true;
      }
    }
    return false;
  };

  const searchYear = (date: dayjs.Dayjs) => {
    for (let i = 0; i < datesList.length; i++) {
      if (datesList[i].year() === date.year()) {
        return true;
      }
    }
    return false;
  };

  let minDate;
  let maxDate;
  if (datesInfo) {
    for (let i = 0; i < datesList.length; i++) {
      if (!minDate || datesList[i] < minDate) {
        minDate = datesList[i];
      }
      if (!maxDate || datesList[i] > maxDate) {
        maxDate = datesList[i];
      }
    }
  }

  let content: ReactNode;

  if (isLoading || isFetching) {
    content = (
      <Typography variant="body2" margin="auto">
        loading available dates...
      </Typography>
    );
  } else if (isError) {
    content = <span>Could not load dates.</span>;
  } else if (datesList) {
    content = (
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en">
        <StaticDatePicker
          className={styles.root}
          value={targetDate ? dayjs(targetDate) : null}
          minDate={minDate}
          maxDate={maxDate}
          reduceAnimations
          shouldDisableDate={(date) => {
            return !searchDate(date);
          }}
          shouldDisableMonth={(month) => {
            return !searchMonth(month);
          }}
          shouldDisableYear={(year) => {
            return !searchYear(year);
          }}
          onChange={handleChange}
          slots={{
            day: StlDay as React.ComponentType<PickersDayProps<Dayjs>>,
          }}
          slotProps={{
            day: {
              vnrtOrRtDates,
              nrtDates,
              startDate: targetDate,
              endDate,
              specialDates: getUniqueDatesFromSpecialDates(specialDates),
              onClick: () => onSelect?.(), // used to close popup only when clicked on a date
            } as any,
          }}
        />
      </LocalizationProvider>
    );
  }
  return content;
}

export function GenericDatePicker({
  disabled = false,
  open,
  setOpen,
  label = null,
  extras = null,
  getButtonTitle,
  enableDayArrows = false,
  enableDayRange = false,
  narrow = true,
  closeOnSelect = false,
  fullWidth = false,
  placeholderText = 'Select',
  buttonClass,
  onClose,
  ...props
}: {
  disabled?: boolean;
  enableDayArrows?: boolean;
  open: boolean;
  setOpen: (val: boolean) => void;
  label?: ReactNode;
  extras?: ReactNode;
  narrow?: boolean;
  closeOnSelect?: boolean;
  getButtonTitle?: () => string;
  fullWidth?: boolean;
  enableDayRange?: boolean;
  placeholderText?: string;
  buttonClass?: string;
  onClose?: () => void;
  setNewRangeDate?: (start: string, end?: string) => void; // used for multi-day selection
} & IGenericNRTDatePicker) {
  const {
    targetDate,
    setNewTargetDate,
    forbiddenDates,
    endDate,
    setNewRangeDate,
  } = props;
  const [specialDatesOpen, setSpecialDatesOpen] = useState<boolean>(false);
  const [dayRangeOpen, setDayRangeOpen] = useState<boolean>(false);
  const project_slug = useAppSelector(selectProject);
  const { data: datesInfo } = useGetDatesQuery(project_slug, {
    skip: !project_slug,
  });

  const commitDate = useCallback(
    (start: string, end: string) => {
      if (setNewRangeDate) {
        setNewRangeDate(start, end);
      } else {
        setNewTargetDate(start);
      }
    },
    [setNewRangeDate, setNewTargetDate],
  );

  // check if range dates includes unavailable days, reset to the latest single date
  useEffect(() => {
    if (!enableDayRange || !datesInfo?.length || !targetDate || !endDate)
      return;
    const allowed = new Set(datesInfo.map((d) => d.date));
    const rangeOk = daysInRange(targetDate, endDate).every((d) =>
      allowed.has(d),
    );
    if (!rangeOk) {
      setNewTargetDate(datesInfo[datesInfo.length - 1].date);
    }
  }, [enableDayRange, datesInfo, targetDate, endDate, setNewTargetDate]);

  const { nextDay, lastDay, nextDayAvailable, lastDayAvailable } =
    useNextLastDay();

  const getDefaultLabel = (): string => {
    if (!targetDate) return placeholderText;
    if (enableDayRange && endDate) {
      return `${targetDate} - ${endDate}`;
    }
    return targetDate;
  };

  const goBackADay = (event) => {
    setNewTargetDate(lastDay);
  };

  const goOnADay = (event) => {
    setNewTargetDate(nextDay);
  };

  const handleSelect = () => {
    if (closeOnSelect) setOpen(false);
  };

  useEffect(() => {
    setSpecialDatesOpen(false);
    setDayRangeOpen(false);
  }, [open]);

  const picker = (
    <StlPopoverWrapper
      disabled={disabled}
      keepMounted
      buttonClass={classNames(fullWidth && styles.fullWidth, buttonClass)}
      narrow={narrow && !dayRangeOpen}
      open={open}
      setOpen={setOpen}
      startIcon={<FontAwesomeIcon name="calendar" size="0.75rem" />}
      renderLabel={getButtonTitle ?? getDefaultLabel}
      isPlaceholderText={!targetDate}
      onClose={onClose}
    >
      <Box className={styles.flexBox}>
        <Stack>
          {!specialDatesOpen && !dayRangeOpen && (
            <>
              <DateTimePickerWrapper
                width={narrow ? '236px' : DATE_PICKER_DEFAULT_SIZE}
                height={narrow ? 'auto' : DATE_PICKER_DEFAULT_SIZE}
                title="Select date"
                component={
                  <>
                    <Typography
                      variant="caption"
                      className={styles.calendar_header}
                    >
                      {label}
                    </Typography>
                    {enableDayRange ? (
                      <MultiDayRangePicker
                        open={open}
                        targetDate={targetDate}
                        endDate={endDate}
                        forbiddenDates={forbiddenDates}
                        onClose={() => {
                          setOpen(false);
                        }}
                        setDate={commitDate}
                        monthsToShow={1}
                        selectRange={false}
                      />
                    ) : (
                      <GenericNRTDatePicker
                        targetDate={targetDate}
                        setNewTargetDate={setNewTargetDate}
                        forbiddenDates={forbiddenDates}
                        allowNoDate={props.allowNoDate}
                        endDate={endDate}
                        onSelect={handleSelect}
                      />
                    )}
                  </>
                }
                extra={
                  <>
                    <SpecialDatesButton
                      handleClick={() => setSpecialDatesOpen((prev) => !prev)}
                    />
                    {enableDayRange && (
                      <DayRangePickerButton
                        handleClick={() => setDayRangeOpen((prev) => !prev)}
                      />
                    )}
                  </>
                }
              />
            </>
          )}
          <SpecialDatesPicker
            forbiddenDates={forbiddenDates}
            targetDate={targetDate}
            endDate={enableDayRange ? endDate : undefined}
            open={specialDatesOpen}
            narrow={narrow}
            onClose={() => setSpecialDatesOpen(false)}
            setNewTargetDate={setNewTargetDate}
            setNewRangeDate={enableDayRange ? setNewRangeDate : undefined}
          />
          {enableDayRange && (
            <MultiDayRangePicker
              open={dayRangeOpen}
              targetDate={targetDate}
              endDate={endDate}
              forbiddenDates={forbiddenDates}
              onClose={() => setDayRangeOpen(false)}
              setDate={commitDate}
            />
          )}
        </Stack>
        {extras}
      </Box>
    </StlPopoverWrapper>
  );

  return enableDayArrows ? (
    <Grid container spacing={0} alignItems="center">
      <Grid item>
        <Tooltip title="Previous day">
          <span>
            <IconButton
              size="small"
              onClick={goBackADay}
              disabled={!lastDayAvailable}
            >
              <ArrowLeftIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Grid>
      <Grid item xs>
        {picker}
      </Grid>
      <Grid item>
        <Tooltip title="Next day">
          <span>
            <IconButton
              size="small"
              onClick={goOnADay}
              disabled={!nextDayAvailable}
            >
              <ArrowRightIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Grid>
    </Grid>
  ) : (
    picker
  );
}

export function NRTDatePicker({
  enableDayArrows = false,
  enableDayRange = true,
}: {
  enableDayArrows?: boolean;
  enableDayRange?: boolean;
}) {
  const [open, setOpen] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const targetDate = useAppSelector(selectTargetDate);
  const targetEndDate = useAppSelector(selectTargetEndDate);

  return (
    <TaskBarComponent>
      <OverflowLabel id="date-picker-input-label">
        Date: &nbsp;
        <GenericDatePicker
          open={open}
          setOpen={setOpen}
          enableDayArrows={enableDayArrows}
          targetDate={targetDate}
          endDate={targetEndDate}
          enableDayRange={enableDayRange}
          setNewTargetDate={(value) => {
            dispatch(setTargetDate(value));
          }}
          setNewRangeDate={(start, end) => {
            dispatch(setStartEndDate({ start, end }));
          }}
          closeOnSelect
          fullWidth
        />
      </OverflowLabel>
    </TaskBarComponent>
  );
}
